// Feldrahmen: Beschriftung über dem Feld, Hinweis oder Fehler darunter. Alle Eingabe-Bausteine nutzen ihn,
// damit Beschriftung, aria-describedby und Fehlerdarstellung überall gleich sind.
import { CircleAlert } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import type { FieldProps } from './types'

export interface FieldIds {
  id: string
  labelId: string
  hintId: string | undefined
  errorId: string | undefined
  describedBy: string | undefined
  invalid: boolean
}

export function useFieldIds(props: Pick<FieldProps, 'id' | 'hint' | 'error'>): FieldIds {
  const auto = useId()
  const id = props.id ?? `f${auto}`
  const hintId = props.hint && !props.error ? `${id}-hint` : undefined
  const errorId = props.error ? `${id}-error` : undefined
  return { id, labelId: `${id}-label`, hintId, errorId, describedBy: errorId ?? hintId, invalid: Boolean(props.error) }
}

export interface FieldFrameProps extends Pick<
  FieldProps,
  'label' | 'labelHidden' | 'hint' | 'error' | 'required' | 'className'
> {
  ids: FieldIds
  /** rechts neben der Beschriftung: Zähler, Umschalter */
  labelEnd?: ReactNode
  /** 'group': Beschriftung ist kein <label for>, sondern benennt eine Gruppe (aria-labelledby) */
  as?: 'field' | 'group'
  children: ReactNode
}

export function FieldFrame({
  ids,
  label,
  labelHidden,
  hint,
  error,
  required,
  labelEnd,
  as = 'field',
  className,
  children,
}: FieldFrameProps) {
  const labelContent = label && (
    <>
      {label}
      {required === false && <span className="ml-1 font-normal text-fg-subtle">optional</span>}
    </>
  )
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)} data-ui="field-frame">
      {label && (
        <div className={cn('flex items-baseline justify-between gap-2', labelHidden && 'sr-only')}>
          {as === 'group' ? (
            <span id={ids.labelId} className="text-caption font-medium text-fg">
              {labelContent}
            </span>
          ) : (
            <label id={ids.labelId} htmlFor={ids.id} className="text-caption font-medium text-fg">
              {labelContent}
            </label>
          )}
          {labelEnd}
        </div>
      )}
      {children}
      {error ? (
        <p id={ids.errorId} className="flex items-start gap-1 text-caption text-danger-fg">
          <CircleAlert className="mt-px size-3.5 shrink-0" strokeWidth={2} aria-hidden />
          <span>{error}</span>
        </p>
      ) : (
        hint && (
          <p id={ids.hintId} className="text-caption text-fg-subtle">
            {hint}
          </p>
        )
      )}
    </div>
  )
}

/** Rahmen eines Eingabefelds (Fläche surface, Rahmen line-control, Fokus field-focus). */
export const controlClass = (options: {
  invalid?: boolean
  disabled?: boolean
  readOnly?: boolean
  size?: 'sm' | 'md' | 'lg'
}): string =>
  cn(
    'flex w-full min-w-0 items-center rounded-sm border bg-surface text-body text-fg transition-[border-color,box-shadow] duration-(--duration-fast) ease-snap compact:text-dense touch:text-touch',
    options.size === 'sm' ? 'h-control-sm' : options.size === 'lg' ? 'h-control-lg' : 'h-control',
    options.invalid ? 'border-danger focus-within:field-focus-danger' : 'border-line-control focus-within:field-focus',
    options.disabled && 'cursor-not-allowed border-line bg-sunken text-fg-disabled',
    options.readOnly && !options.disabled && 'border-line bg-sunken',
  )

/** Abschnitt eines Formulars mit Überschrift und Erläuterung links bzw. darüber. */
export function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: string
  description?: string
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={cn('flex flex-col gap-(--stack-gap) border-t border-line pt-6 first:border-t-0 first:pt-0', className)}
    >
      <header className="flex flex-col gap-0.5">
        <h3 className="text-body font-semibold text-fg">{title}</h3>
        {description && <p className="text-dense text-fg-muted">{description}</p>}
      </header>
      {children}
    </section>
  )
}

/** Feste Aktionsleiste am unteren Rand eines Formulars: Zustand links, Aktionen rechts. */
export function StickyActionBar({
  status,
  children,
  className,
}: {
  status?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'sticky bottom-0 z-(--z-sticky) flex items-center justify-between gap-3 border-t border-line bg-surface px-(--page-px) pt-3 pb-safe-bar',
        className,
      )}
      // Kennung für den Hinweisbereich (Toast.tsx): Solange eine Aktionsleiste steht, erscheinen Hinweise über ihr
      data-ui="action-bar"
      data-print="hide"
    >
      <div className="min-w-0 text-dense text-fg-muted" aria-live="polite">
        {status}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  )
}
